import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import cidadesData from './cidades.json';
import './PropertySearchBar.css';

// Build districts array from cidades.json keys
const districts = Object.keys(cidadesData);

const translations = {
  en: {
    keywords: "Keywords",
    category: "Category",
    district: "District",
    buy: "Buy",
    rent: "Rent",
    search: "Search",
    allCategories: "All Categories",
    allDistricts: "All Districts",
    city: "City",
    allCities: "All Cities",
    categories: [
      "Apartments",
      "Rooms",
      "Houses",
      "Lands",
      "Farms and Estates",
      "Garages",
      "Buildings",
      "Offices",
      "Warehouses",
      "Stores",
    ],
  },
  pt: {
    keywords: "Palavras-chave",
    category: "Categoria",
    district: "Distrito",
    buy: "Comprar",
    rent: "Arrendar",
    search: "Pesquisar",
    allCategories: "Todas as Categorias",
    allDistricts: "Todos os Distritos",
    city: "Cidade",
    allCities: "Todas as Cidades",
    categories: [
      "Apartamentos",
      "Quartos",
      "Moradias",
      "Terrenos",
      "Quintas e Herdades",
      "Garagens",
      "Prédios",
      "Escritórios",
      "Armazéns",
      "Lojas",
    ],
  },
  fr: {
    keywords: "Mots-clés",
    category: "Catégorie",
    district: "District",
    buy: "Acheter",
    rent: "Louer",
    search: "Rechercher",
    allCategories: "Toutes les Catégories",
    allDistricts: "Tous les Districts",
    city: "Ville",
    allCities: "Toutes les Villes",
    categories: [
      "Appartements",
      "Chambres",
      "Maisons",
      "Terrains",
      "Fermes et Domaines",
      "Garages",
      "Bâtiments",
      "Bureaux",
      "Entrepôts",
      "Magasins",
    ],
  },
};

const PropertySearchBar = ({ onSearch }) => {
  const [searchText, setSearchText] = useState('');
  const [category, setCategory] = useState('');
  const [district, setDistrict] = useState('');
  const [city, setCity] = useState('');
  const [cities, setCities] = useState([]);
  const [businessType, setSearchType] = useState('Comprar'); // Default to 'Comprar'
  const [language, setLanguage] = useState(localStorage.getItem("language") || "pt");
  const navigate = useNavigate();

  const t = translations[language];

  useEffect(() => {
    const handleLanguageChange = () => {
      const newLanguage = localStorage.getItem("language") || "pt";
      setLanguage(newLanguage);
    };
    window.addEventListener("languageChange", handleLanguageChange);
    return () => {
      window.removeEventListener("languageChange", handleLanguageChange);
    };
  }, []);

  // Update cities when district changes
  useEffect(() => {
    if (district) {
      setCities(cidadesData[district] || []);
    } else {
      setCities([]);
    }
    setCity('');
  }, [district]);

  // Handle search and pass filters to the parent component
  const handleSearch = () => {
    const filters = {
      searchText: searchText.trim(),
      category,
      district,
      city,
      business_type: businessType,
    };
    // Send cities array as a separate property in the navigation state
    const citiesArray = district ? (cidadesData[district] || []) : [];
    localStorage.setItem('redirectFromSearch', 'true');
    if (onSearch) {
      onSearch(filters);
    }
    navigate(`/imoveis`, { state: { ...filters, citiesArray } });
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    localStorage.setItem("businessType", type); // Save the selected type to localStorage
  };

  return (
    <div className="search-bar-container">
      <div className="search-type-tabs" style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          className={`search-type-button ${businessType === 'Comprar' ? 'active' : ''}`}
          onClick={() => handleSearchTypeChange('Comprar')}
        >
          {t.buy}
        </button>
        <button
          className={`search-type-button ${businessType === 'Arrendar' ? 'active' : ''}`}
          onClick={() => handleSearchTypeChange('Arrendar')}
        >
          {t.rent}
        </button>
      </div>
      
      {/*<div className="search-field">
        <label htmlFor="searchText" className="search-label">
          {t.keywords}
        </label>
        <input
          id="searchText"
          type="text"
          placeholder={t.keywords}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="search-bar-input"
        />
      </div>*/}

      <div className="search-field">
        <label htmlFor="category" className="search-label">
          {t.category}
        </label>
        <select
          id="category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="search-bar-dropdown"
        >
          <option value="">{t.allCategories}</option>
          {t.categories.map((cat, index) => (
            <option key={index} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      <div className="search-field">
        <label htmlFor="district" className="search-label">
          {t.district}
        </label>
        <select
          id="district"
          value={district}
          onChange={(e) => setDistrict(e.target.value)}
          className="search-bar-dropdown"
        >
          <option value="">{t.allDistricts}</option>
          {districts.map((districtName, index) => (
            <option key={index} value={districtName}>
              {districtName}
            </option>
          ))}
        </select>
      </div>

      <div className="search-field">
        <label htmlFor="city" className="search-label">
          {t.city}
        </label>
        <select
          id="city"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          className="search-bar-dropdown"
          disabled={!district}
        >
          <option value="">{t.allCities}</option>
          {cities.map((cityName, idx) => (
            <option key={idx} value={cityName}>
              {cityName}
            </option>
          ))}
        </select>
      </div>

      <div className="search-field">
        <label className="search-label hidden-label">&nbsp;</label>
        <button onClick={handleSearch} className="search-bar-button">
          {t.search}
        </button>
      </div>
    </div>
  );
};

export default PropertySearchBar;
